/**
 * The camera back: temporal accumulation, defocus, veiling glare, lens artefacts
 * and the tone curve — one chain, feeding both the canvas and the export.
 *
 * Three things this fixes at once.
 *
 * The vignette and grain used to exist twice and in the wrong place: a CSS
 * radial gradient plus a soft-light noise tile over the canvas, and a second
 * hand-written pair on the Canvas 2D export at a different relative scale. Both
 * multiplied *encoded* pixels, so the vignette darkened without ever behaving
 * like illumination falling off, and the preview was never the result. Here they
 * are one implementation, applied in linear light before the curve, at the same
 * scale in both destinations.
 *
 * Nothing was ever accumulated. The render loop already redraws only when
 * something changed, which is exactly the condition temporal accumulation wants:
 * once the camera settles, sixty-four jittered samples resolve the spires' edges
 * far past what any single-frame filter can, for free.
 *
 * And there was no defocus at all. For a table-top object under a hundred
 * millimetre lens that is the single loudest omission — a real photograph of this
 * subject has a visible focal plane, and the eye reads its absence as synthetic
 * long before it notices anything about the shading.
 */

import * as THREE from "three";

import {
  APERTURE,
  BARREL_DISTORTION,
  CHROMATIC_ABERRATION,
  GLARE_STRENGTH,
  GLARE_THRESHOLD,
  GRAIN_AMOUNT,
  TAA_SAMPLES,
  VIGNETTE_STRENGTH,
} from "../../shared/constants.ts";

/**
 * Above this many pixels the multisample buffers cost more than they are worth:
 * an export runs to full convergence anyway, and sixty-four jittered samples
 * resolve an edge better than four coverage levels ever could.
 */
const MULTISAMPLE_PIXEL_LIMIT = 4_000_000;

/** Glare is a wide, faint halo, so a quarter of each axis loses nothing. */
const GLARE_DIVISOR = 4;

const quadVertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

/** Running mean, so the image is presentable at every sample count. */
const accumulateShader = /* glsl */ `
uniform sampler2D uPrevious;
uniform sampler2D uCurrent;
uniform float uWeight;
varying vec2 vUv;
void main() {
  vec4 previous = texture2D(uPrevious, vUv);
  vec4 current = texture2D(uCurrent, vUv);
  gl_FragColor = mix(previous, current, uWeight);
}
`;

const defocusShader = /* glsl */ `
uniform sampler2D uSource;
uniform sampler2D uDepth;
uniform vec2 uResolution;
uniform float uNear;
uniform float uFar;
uniform float uFocus;
uniform float uAperture;
uniform float uDepthRange;
varying vec2 vUv;

const int TAPS = 32;
const float GOLDEN_ANGLE = 2.39996323;

float viewDistance(vec2 at) {
  float depth = texture2D(uDepth, at).x;
  float ndc = depth * 2.0 - 1.0;
  return (2.0 * uNear * uFar) / (uFar + uNear - ndc * (uFar - uNear));
}

/**
 * Signed circle of confusion in pixels; negative in front of the focal plane.
 *
 * Normalised by the subject's own depth, so the aperture states what fraction of
 * the object stays sharp. An absolute multiplier could not: the relief spans a
 * sixth of the camera's throw seen from above and well over half of it in
 * perspective, so one setting was invisible in the first and overwhelming in the
 * second.
 */
float circleOfConfusion(vec2 at) {
  float distance = viewDistance(at);
  float relative = (distance - uFocus) / max(uDepthRange, 1e-6);
  return clamp(relative, -1.0, 1.0) * uAperture * uResolution.y;
}

void main() {
  float centreCoc = circleOfConfusion(vUv);
  float radius = abs(centreCoc);
  if (radius < 1.0) {
    gl_FragColor = texture2D(uSource, vUv);
    return;
  }

  vec4 total = texture2D(uSource, vUv);
  float weightSum = 1.0;
  for (int tap = 0; tap < TAPS; tap++) {
    float fraction = (float(tap) + 0.5) / float(TAPS);
    float angle = float(tap) * GOLDEN_ANGLE;
    // Square root keeps the spiral's samples uniform per unit area rather than
    // per unit radius, which is what an aperture actually is.
    vec2 offset = vec2(cos(angle), sin(angle)) * sqrt(fraction) * radius;
    vec2 at = vUv + offset / uResolution;
    // A sample only contributes where its own blur circle reaches this pixel;
    // without that test a blurred background bleeds over a sharp foreground.
    float reach = abs(circleOfConfusion(at));
    float weight = clamp(reach - length(offset) + 1.0, 0.0, 1.0);
    total += texture2D(uSource, at) * weight;
    weightSum += weight;
  }
  gl_FragColor = total / weightSum;
}
`;

const brightShader = /* glsl */ `
uniform sampler2D uSource;
uniform float uThreshold;
varying vec2 vUv;
void main() {
  vec3 colour = texture2D(uSource, vUv).rgb;
  gl_FragColor = vec4(max(colour - uThreshold, vec3(0.0)), 1.0);
}
`;

const blurShader = /* glsl */ `
uniform sampler2D uSource;
uniform vec2 uStep;
varying vec2 vUv;
void main() {
  // Nine-tap Gaussian on linear-filtered pairs, i.e. a seventeen-tap footprint.
  vec3 total = texture2D(uSource, vUv).rgb * 0.2270270270;
  total += (texture2D(uSource, vUv + uStep * 1.3846153846).rgb +
            texture2D(uSource, vUv - uStep * 1.3846153846).rgb) * 0.3162162162;
  total += (texture2D(uSource, vUv + uStep * 3.2307692308).rgb +
            texture2D(uSource, vUv - uStep * 3.2307692308).rgb) * 0.0702702703;
  gl_FragColor = vec4(total, 1.0);
}
`;

const outputShader = /* glsl */ `
#include <common>
#include <tonemapping_pars_fragment>
#include <dithering_pars_fragment>

uniform sampler2D uSource;
uniform sampler2D uGlare;
uniform vec2 uTanHalf;
uniform float uBarrel;
uniform float uAberration;
uniform float uVignette;
uniform float uGlareStrength;
uniform float uGrain;
varying vec2 vUv;

float outputHash(vec2 point) {
  return fract(sin(dot(point, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec2 centred = vUv - 0.5;
  float radiusSq = dot(centred, centred);
  vec2 distorted = centred * (1.0 + uBarrel * radiusSq);

  // Lateral chromatic aberration: the three channels focus at slightly different
  // magnifications, so the split is radial and grows towards the corners.
  vec3 colour;
  colour.r = texture2D(uSource, 0.5 + distorted * (1.0 + uAberration)).r;
  colour.g = texture2D(uSource, 0.5 + distorted).g;
  colour.b = texture2D(uSource, 0.5 + distorted * (1.0 - uAberration)).b;

  colour += texture2D(uGlare, 0.5 + distorted).rgb * uGlareStrength;

  // Natural illumination falloff: cos^4 of the field angle, in linear light and
  // before the curve, which is the only place it behaves like light.
  float tangent = length(centred * 2.0 * uTanHalf);
  float cosine = inversesqrt(1.0 + tangent * tangent);
  float falloff = cosine * cosine * cosine * cosine;
  colour *= mix(1.0, falloff, uVignette);

  colour = AgXToneMapping(colour * toneMappingExposure);

  // Film grain lives in the image, strongest through the mid-tones where emulsion
  // actually is. Seeded by pixel alone, so an export is reproducible.
  float luminance = dot(colour, vec3(0.2126, 0.7152, 0.0722));
  float weight = 1.0 - abs(luminance * 2.0 - 1.0);
  colour += (outputHash(gl_FragCoord.xy) - 0.5) * uGrain * weight;

  gl_FragColor = vec4(clamp(colour, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
  #include <dithering_fragment>
}
`;

/** Halton, the standard low-discrepancy choice for sub-pixel offsets. */
function halton(index: number, base: number): number {
  let result = 0;
  let fraction = 1;
  let current = index;
  while (current > 0) {
    fraction /= base;
    result += fraction * (current % base);
    current = Math.floor(current / base);
  }
  return result;
}

const JITTER: ReadonlyArray<readonly [number, number]> = Array.from(
  { length: TAA_SAMPLES },
  (_, index) => [halton(index + 1, 2) - 0.5, halton(index + 1, 3) - 0.5] as const,
);

export interface PresentRequest {
  /** Distance from the camera to the plane in focus, in world units. */
  focus: number;
  /** Circle-of-confusion radius at full defocus, as a fraction of frame height. */
  aperture: number;
  /** The subject's own depth, which is what the aperture is measured against. */
  depthRange: number;
  camera: THREE.PerspectiveCamera;
}

export interface Compositor {
  /** Where the scene must be rendered. */
  readonly sceneTarget: THREE.WebGLRenderTarget;
  setSize(width: number, height: number): void;
  /** True while more samples are wanted. */
  readonly converged: boolean;
  /** Applies the next sub-pixel offset to the camera. Call before rendering. */
  jitter(camera: THREE.PerspectiveCamera): void;
  /** Folds the rendered frame into the running mean. */
  accumulate(): void;
  /** Discards the accumulation; the camera or the scene changed. */
  restart(): void;
  /** Runs defocus, glare and output. `null` presents to the canvas. */
  present(output: THREE.WebGLRenderTarget | null, request: PresentRequest): void;
  dispose(): void;
}

export function createCompositor(renderer: THREE.WebGLRenderer): Compositor {
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
  const quadScene = new THREE.Scene();
  quadScene.add(quad);
  const quadCamera = new THREE.Camera();

  const colourOptions = {
    type: THREE.HalfFloatType,
    format: THREE.RGBAFormat,
    depthBuffer: false,
    stencilBuffer: false,
    generateMipmaps: false,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
  } as const;

  let sceneTarget = new THREE.WebGLRenderTarget(1, 1, {
    ...colourOptions,
    depthBuffer: true,
  });
  sceneTarget.depthTexture = new THREE.DepthTexture(1, 1);
  const accumulation: THREE.WebGLRenderTarget[] = [
    new THREE.WebGLRenderTarget(1, 1, colourOptions),
    new THREE.WebGLRenderTarget(1, 1, colourOptions),
  ];
  const defocused = new THREE.WebGLRenderTarget(1, 1, colourOptions);
  const glare: THREE.WebGLRenderTarget[] = [
    new THREE.WebGLRenderTarget(1, 1, colourOptions),
    new THREE.WebGLRenderTarget(1, 1, colourOptions),
  ];
  for (const target of [sceneTarget, ...accumulation, defocused, ...glare]) {
    target.texture.colorSpace = THREE.NoColorSpace;
  }

  const passes = {
    accumulate: new THREE.ShaderMaterial({
      uniforms: {
        uPrevious: { value: null },
        uCurrent: { value: null },
        uWeight: { value: 1 },
      },
      vertexShader: quadVertexShader,
      fragmentShader: accumulateShader,
      depthTest: false,
      depthWrite: false,
    }),
    defocus: new THREE.ShaderMaterial({
      uniforms: {
        uSource: { value: null },
        uDepth: { value: null },
        uResolution: { value: new THREE.Vector2() },
        uNear: { value: 0.1 },
        uFar: { value: 100 },
        uFocus: { value: 1 },
        uAperture: { value: APERTURE },
        uDepthRange: { value: 1 },
      },
      vertexShader: quadVertexShader,
      fragmentShader: defocusShader,
      depthTest: false,
      depthWrite: false,
    }),
    bright: new THREE.ShaderMaterial({
      uniforms: { uSource: { value: null }, uThreshold: { value: GLARE_THRESHOLD } },
      vertexShader: quadVertexShader,
      fragmentShader: brightShader,
      depthTest: false,
      depthWrite: false,
    }),
    blur: new THREE.ShaderMaterial({
      uniforms: { uSource: { value: null }, uStep: { value: new THREE.Vector2() } },
      vertexShader: quadVertexShader,
      fragmentShader: blurShader,
      depthTest: false,
      depthWrite: false,
    }),
    output: new THREE.ShaderMaterial({
      uniforms: {
        uSource: { value: null },
        uGlare: { value: null },
        uTanHalf: { value: new THREE.Vector2() },
        uBarrel: { value: BARREL_DISTORTION },
        uAberration: { value: CHROMATIC_ABERRATION },
        uVignette: { value: VIGNETTE_STRENGTH },
        uGlareStrength: { value: GLARE_STRENGTH },
        uGrain: { value: GRAIN_AMOUNT },
      },
      vertexShader: quadVertexShader,
      fragmentShader: outputShader,
      depthTest: false,
      depthWrite: false,
      dithering: true,
    }),
  };

  let width = 1;
  let rows = 1;
  let sample = 0;
  let front = 0;

  const draw = (material: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget | null) => {
    quad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(quadScene, quadCamera);
  };

  return {
    get sceneTarget(): THREE.WebGLRenderTarget {
      return sceneTarget;
    },

    get converged(): boolean {
      return sample >= TAA_SAMPLES;
    },

    setSize(nextWidth: number, nextRows: number): void {
      if (nextWidth === width && nextRows === rows) return;
      width = Math.max(1, Math.floor(nextWidth));
      rows = Math.max(1, Math.floor(nextRows));

      // Multisampling is a property of the buffer, so a change of sample count
      // means a new one rather than a resize.
      const wanted = width * rows > MULTISAMPLE_PIXEL_LIMIT ? 0 : 4;
      if (sceneTarget.samples !== wanted) {
        sceneTarget.dispose();
        sceneTarget = new THREE.WebGLRenderTarget(width, rows, {
          ...colourOptions,
          depthBuffer: true,
          samples: wanted,
        });
        sceneTarget.texture.colorSpace = THREE.NoColorSpace;
        sceneTarget.depthTexture = new THREE.DepthTexture(width, rows);
      } else {
        sceneTarget.setSize(width, rows);
      }

      for (const target of accumulation) target.setSize(width, rows);
      defocused.setSize(width, rows);
      const glareWidth = Math.max(1, Math.floor(width / GLARE_DIVISOR));
      const glareRows = Math.max(1, Math.floor(rows / GLARE_DIVISOR));
      for (const target of glare) target.setSize(glareWidth, glareRows);
      sample = 0;
    },

    restart(): void {
      sample = 0;
    },

    jitter(camera: THREE.PerspectiveCamera): void {
      const [offsetX, offsetY] = JITTER[sample % TAA_SAMPLES];
      // A view offset in fractional pixels is exactly a sub-pixel shift of the
      // projection, with no need to touch the projection matrix by hand.
      camera.setViewOffset(width, rows, offsetX, offsetY, width, rows);
    },

    accumulate(): void {
      const previous = accumulation[front];
      const next = accumulation[1 - front];
      passes.accumulate.uniforms.uPrevious.value = previous.texture;
      passes.accumulate.uniforms.uCurrent.value = sceneTarget.texture;
      passes.accumulate.uniforms.uWeight.value = sample === 0 ? 1 : 1 / (sample + 1);
      draw(passes.accumulate, next);
      front = 1 - front;
      sample += 1;
    },

    present(output: THREE.WebGLRenderTarget | null, request: PresentRequest): void {
      const accumulated = accumulation[front].texture;

      const defocus = passes.defocus.uniforms;
      defocus.uSource.value = accumulated;
      defocus.uDepth.value = sceneTarget.depthTexture;
      (defocus.uResolution.value as THREE.Vector2).set(width, rows);
      defocus.uNear.value = request.camera.near;
      defocus.uFar.value = request.camera.far;
      defocus.uFocus.value = request.focus;
      defocus.uAperture.value = request.aperture;
      defocus.uDepthRange.value = request.depthRange;
      draw(passes.defocus, defocused);

      passes.bright.uniforms.uSource.value = defocused.texture;
      draw(passes.bright, glare[0]);
      const glareWidth = glare[0].width;
      const glareRows = glare[0].height;
      (passes.blur.uniforms.uStep.value as THREE.Vector2).set(1 / glareWidth, 0);
      passes.blur.uniforms.uSource.value = glare[0].texture;
      draw(passes.blur, glare[1]);
      (passes.blur.uniforms.uStep.value as THREE.Vector2).set(0, 1 / glareRows);
      passes.blur.uniforms.uSource.value = glare[1].texture;
      draw(passes.blur, glare[0]);

      const tanY = Math.tan(THREE.MathUtils.degToRad(request.camera.fov) * 0.5);
      const out = passes.output.uniforms;
      out.uSource.value = defocused.texture;
      out.uGlare.value = glare[0].texture;
      (out.uTanHalf.value as THREE.Vector2).set(tanY * request.camera.aspect, tanY);
      draw(passes.output, output);
      renderer.setRenderTarget(null);
    },

    dispose(): void {
      quad.geometry.dispose();
      for (const material of Object.values(passes)) material.dispose();
      sceneTarget.depthTexture?.dispose();
      sceneTarget.dispose();
      for (const target of accumulation) target.dispose();
      defocused.dispose();
      for (const target of glare) target.dispose();
    },
  };
}
